/**
 * src/three/BrickWall3D.js
 * กำแพงกล่องสี่เหลี่ยมหลายชั้น — ลายหิน + เป้าเป้าหมาย
 */
import * as THREE from 'three';
import { T3, TARGET_LABELS } from './config.js';
import { createStoneMaterial } from './stoneTexture.js';
import { createTargetSprite } from './targetLabel.js';

function randInt(a, b) {
  return a + Math.floor(Math.random() * (b - a + 1));
}

export default class BrickWall3D {
  constructor(scene) {
    this.scene = scene;
    this.group = new THREE.Group();
    scene.add(this.group);

    this.mats = [];
    this.matCracked = null;

    const { BRICK_W: w, BRICK_H: h, BRICK_D: d } = T3;
    this.geo = new THREE.BoxGeometry(w, h, d);

    // ไฮไลต์เล็ง — ครอบกล่องสี่เหลี่ยม
    this.aimHighlight = new THREE.Mesh(
      new THREE.BoxGeometry(w * 1.06, h * 1.06, d * 1.06),
      new THREE.MeshBasicMaterial({
        color: 0xb8f0a8,
        transparent: true,
        opacity: 0.32,
        depthWrite: false,
        toneMapped: false,
      })
    );
    this.aimHighlight.visible = false;
    this.aimHighlight.renderOrder = 20;

    this.aimEdge = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(w * 1.07, h * 1.07, d * 1.07)),
      new THREE.LineBasicMaterial({
        color: 0xd8ffc8,
        transparent: true,
        opacity: 0.85,
        depthTest: true,
        toneMapped: false,
      })
    );
    this.aimEdge.visible = false;
    this.aimEdge.renderOrder = 21;
    this.group.add(this.aimHighlight);
    this.group.add(this.aimEdge);
    this.aimedBrick = null;
    this.aimHighlight.raycast = () => {};
    this.aimEdge.raycast = () => {};

    /** @type {(object|null)[][][]} */
    this.grid = [];
    this.remaining = 0;
    this.total = 0;
    this.wave = 1;
  }

  async initMaterials(onStep) {
    const defs = [
      { base: [176, 108, 62], seed: 11 },
      { base: [148, 88, 52], seed: 33 },
      { base: [198, 128, 74], seed: 44 },
      { base: [126, 74, 44], seed: 55 },
    ];
    this.mats = [];
    for (const def of defs) {
      this.mats.push(createStoneMaterial(def));
      onStep?.();
      await new Promise((r) => requestAnimationFrame(r));
    }
    this.matCracked = createStoneMaterial({
      base: [112, 68, 42], seed: 22, crack: true,
    });
    onStep?.();
    await new Promise((r) => requestAnimationFrame(r));
  }

  cellW() {
    return T3.BRICK_W + T3.GAP;
  }

  cellH() {
    return T3.BRICK_H + T3.GAP;
  }

  /** แถวจัตุรัสเรียงตรง — ไม่สลับแถว */
  worldPos(col, row, layer) {
    const cw = this.cellW();
    const ch = this.cellH();
    const totalW = T3.COLS * cw;
    const totalH = T3.ROWS * ch;
    const x = -totalW / 2 + cw * 0.5 + col * cw;
    const y = -totalH / 2 + ch * 0.5 + row * ch + 0.15;
    const z = T3.WALL_Z - layer * (T3.BRICK_D * 1.05);
    return { x, y, z };
  }

  spawnWall() {
    this.clear();
    this.grid = [];
    this.total = T3.COLS * T3.ROWS * T3.LAYERS;
    this.remaining = this.total;

    for (let layer = T3.LAYERS - 1; layer >= 0; layer--) {
      this.grid[layer] = [];
      for (let row = 0; row < T3.ROWS; row++) {
        this.grid[layer][row] = [];
        for (let col = 0; col < T3.COLS; col++) {
          const brick = this.createBrick(col, row, layer);
          this.grid[layer][row][col] = brick;
        }
      }
    }
    this.refreshExposure();
  }

  createBrick(col, row, layer) {
    const matIndex = (col + row * 3 + layer) % this.mats.length;
    const mesh = new THREE.Mesh(this.geo, this.mats[matIndex].clone());
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    // โทนแปรผันเล็กน้อยต่อก้อน — น้ำตาลอุ่น
    const tint = 0.9 + Math.random() * 0.18;
    mesh.material.color.setRGB(tint, tint * 0.92, tint * 0.82);

    if (Math.random() < 0.16) {
      mesh.material.color.multiply(new THREE.Color(0xe8c090));
    } else if (Math.random() < 0.14) {
      mesh.material.color.multiply(new THREE.Color(0x8a5a38));
    }

    const { x, y, z } = this.worldPos(col, row, layer);
    mesh.position.set(x, y, z);

    const hp = randInt(
      Math.min(14, T3.HP_MIN + (this.wave - 1) + layer),
      Math.min(20, T3.HP_MAX + (this.wave - 1) + layer)
    );

    const brick = {
      mesh,
      col,
      row,
      layer,
      hp,
      maxHp: hp,
      cracked: false,
      exposed: layer === 0,
      alive: true,
      targetLabel: null,
      targetSprite: null,
    };

    // อิฐชั้นหน้าบางก้อนมีใบหน้า + คำเป้าหมาย
    if (layer === 0 && Math.random() < (T3.TARGET_CHANCE ?? 0.22)) {
      const label = TARGET_LABELS[Math.floor(Math.random() * TARGET_LABELS.length)];
      const spr = createTargetSprite(label);
      mesh.add(spr);
      brick.targetLabel = label;
      brick.targetSprite = spr;
    }

    mesh.userData.brick = brick;
    this.group.add(mesh);
    return brick;
  }

  refreshExposure() {
    for (let row = 0; row < T3.ROWS; row++) {
      for (let col = 0; col < T3.COLS; col++) {
        let found = false;
        for (let layer = 0; layer < T3.LAYERS; layer++) {
          const b = this.grid[layer]?.[row]?.[col];
          if (!b || !b.alive) continue;
          const wasExposed = b.exposed;
          b.exposed = !found;
          // ชั้นใหม่ที่โผล่มา — โอกาสติดเป้า
          if (b.exposed && !wasExposed && !b.targetLabel && layer > 0
            && Math.random() < (T3.TARGET_CHANCE ?? 0.22) * 0.7) {
            const label = TARGET_LABELS[Math.floor(Math.random() * TARGET_LABELS.length)];
            const spr = createTargetSprite(label);
            b.mesh.add(spr);
            b.targetLabel = label;
            b.targetSprite = spr;
          }
          found = true;
        }
      }
    }
  }

  getFrontBrick(col, row) {
    for (let layer = 0; layer < T3.LAYERS; layer++) {
      const b = this.grid[layer]?.[row]?.[col];
      if (b?.alive) return b;
    }
    return null;
  }

  /**
   * ขุดดาเมจที่ตำแหน่ง col,row
   * @returns {{ dealt: number, destroyed: object[], lastHit: object|null }}
   */
  dig(col, row, damage) {
    let remaining = damage;
    let dealt = 0;
    const destroyed = [];
    let lastHit = null;

    for (let layer = 0; layer < T3.LAYERS && remaining > 0; layer++) {
      const b = this.grid[layer]?.[row]?.[col];
      if (!b?.alive || !b.exposed) continue;

      const take = Math.min(b.hp, remaining);
      b.hp -= take;
      remaining -= take;
      dealt += take;
      lastHit = b;

      if (!b.cracked && b.hp <= b.maxHp * 0.5) {
        b.cracked = true;
        b.mesh.material.dispose();
        b.mesh.material = this.matCracked.clone();
      }

      if (b.hp <= 0) {
        b.alive = false;
        b.exposed = false;
        if (b.targetSprite) {
          b.mesh.remove(b.targetSprite);
          b.targetSprite.geometry?.dispose?.();
          b.targetSprite.material?.map?.dispose?.();
          b.targetSprite.material?.dispose?.();
        }
        this.group.remove(b.mesh);
        if (b.mesh.material && b.mesh.material !== this.mats[0]) {
          b.mesh.material.map?.dispose?.();
          b.mesh.material.dispose?.();
        }
        destroyed.push(b);
        this.grid[layer][row][col] = null;
        this.remaining -= 1;
        this.refreshExposure();
      }
      break;
    }

    return { dealt, destroyed, lastHit };
  }

  /** ครอบก้อนที่เล็งด้วยสีเขียวอ่อน */
  setAimHighlight(brick) {
    if (!brick?.alive || !brick.exposed) {
      this.aimedBrick = null;
      this.aimHighlight.visible = false;
      this.aimEdge.visible = false;
      return;
    }
    this.aimedBrick = brick;
    const { x, y, z } = brick.mesh.position;
    this.aimHighlight.position.set(x, y, z);
    this.aimHighlight.rotation.copy(brick.mesh.rotation);
    this.aimEdge.position.set(x, y, z);
    this.aimEdge.rotation.copy(brick.mesh.rotation);
    this.aimHighlight.visible = true;
    this.aimEdge.visible = true;
  }

  brickFromHit(object) {
    let o = object;
    while (o) {
      if (o === this.aimHighlight || o === this.aimEdge) return null;
      if (o.userData?.brick) return o.userData.brick;
      o = o.parent;
    }
    return null;
  }

  getRemaining() {
    return this.remaining;
  }

  getTotal() {
    return this.total || (T3.COLS * T3.ROWS * T3.LAYERS);
  }

  clear() {
    const keep = new Set([this.aimHighlight, this.aimEdge]);
    for (let i = this.group.children.length - 1; i >= 0; i--) {
      const c = this.group.children[i];
      if (keep.has(c)) continue;
      this.group.remove(c);
      c.traverse?.((obj) => {
        if (obj === c) return;
        obj.geometry?.dispose?.();
        if (obj.material?.map) obj.material.map.dispose();
        if (obj.material) obj.material.dispose?.();
      });
      if (c.material?.map) c.material.map.dispose();
      if (c.material) c.material.dispose?.();
    }
    this.grid = [];
    this.setAimHighlight(null);
  }

  dispose() {
    this.clear();
    this.geo.dispose();
    this.mats.forEach((m) => {
      m.map?.dispose();
      m.dispose();
    });
    this.matCracked?.map?.dispose();
    this.matCracked?.dispose();
    this.aimHighlight.geometry.dispose();
    this.aimHighlight.material.dispose();
    this.aimEdge.geometry.dispose();
    this.aimEdge.material.dispose();
    this.group.remove(this.aimHighlight);
    this.group.remove(this.aimEdge);
    this.scene.remove(this.group);
  }
}
